// Portfolio
import React, { useEffect, useState } from 'react';
import styles from './Portfolio.module.sass';
import cn from 'classnames';
import Button from 'components/common/Button';
import Icon from 'components/common/Icon';
import { GuidanceTips } from 'constants/profile';
import { CreatorPortfolio } from 'types/creator';
import PortfolioItem from './PortfolioItem';
import PreviewModal from './PreviewModal';
import Loader from 'components/common/Loader';
import { trackEvent } from 'services/analytics';
import { CreatorProfileType } from 'types/profile';
import { useRouter } from 'next/router';
import { MediaItem } from 'types/content';

export interface PortfolioProps {
  creatorId: string;
  portfolio: CreatorPortfolio;
  creatorName: string;
  profilePicture: string;
  profileType: CreatorProfileType;
  editItem?: (portfolioId: MediaItem) => void;
  removeItem?: (portfolioId: MediaItem) => void;
  handleAddContent?: () => void;
  closePersonalModals?: () => void;
  updatingPortfolio?: boolean;
}

enum PORTFOLIO_TABS {
  ALL = 'all',
  PHOTOS = 'photos',
  VIDEOS = 'videos',
}

const Portfolio: React.FC<PortfolioProps> = ({
  creatorId,
  portfolio = [],
  creatorName,
  profilePicture,
  profileType,
  // Personal Portfolio Props
  editItem,
  removeItem,
  handleAddContent,
  closePersonalModals,
  updatingPortfolio,
}) => {
  const router = useRouter();
  const [previewMedia, setPreviewMedia] = useState<MediaItem | undefined>();
  const [tab, setTab] = useState<PORTFOLIO_TABS>(PORTFOLIO_TABS.ALL);
  useEffect(() => {
    setPreviewMedia(undefined);
  }, [portfolio]);

  useEffect(() => {
    if (router.isReady && router.query.portfolio_item) {
      console.log(`url preview: ${router.query.portfolio_item}`);
      const url = router.query.portfolio_item as string;
      const mediaItem = portfolio.find((item) => item.url === url);
      if (mediaItem) {
        previewItem(mediaItem);
      }
    }
  }, [router.isReady]);

  const handleTabChange = (tabName: PORTFOLIO_TABS) => {
    setTab(tabName);
    trackEvent(
      `${
        profileType === 'personal' ? 'Creator' : 'Visitor'
      } Selects Portfolio Tab`,
      { tabName }
    );
  };

  const handleClose = () => {
    setPreviewMedia(undefined);
    if (closePersonalModals) {
      closePersonalModals();
    }
  };

  const onRemove = (mediaItem: MediaItem) => {
    if (removeItem) {
      handleClose();
      removeItem(mediaItem);
    }
  };
  const onEdit = (mediaItem: MediaItem) => {
    if (editItem) {
      handleClose();
      editItem(mediaItem);
    }
  };

  const previewItem = (mediaItem: MediaItem) => {
    setPreviewMedia(mediaItem);
    trackEvent(
      `${
        profileType === 'personal' ? 'Creator' : 'Visitor'
      } Views Portfolio Item`,
      { ...mediaItem, profileType }
    );
  };

  return (
    <>
      <div className={styles.portfolio}>
        {profileType === 'iframe' && <h5>Portfolio</h5>}
        <div className={styles.cardHeader}>
          {/* Tabs */}
          <div className={styles.tabContainer}>
            {Object.values(PORTFOLIO_TABS).map((tabKey) => (
              <div
                key={tabKey}
                className={cn(styles.tab, {
                  [styles.selected]: tab === tabKey,
                })}
                onClick={() => handleTabChange(tabKey)}
              >
                {tabKey}
              </div>
            ))}
          </div>
          {/* Add Content Button */}
          {profileType === 'personal' && (
            <Button
              type="primary"
              className={styles.addContentBtn}
              onClick={handleAddContent}
            >
              Add Photos or Videos + <Icon name="photos" />
            </Button>
          )}
        </div>
        {/* Guidance Section */}
        {profileType === 'personal' && (
          <div className={styles.guidanceSection}>
            <p>
              Show off the best work you have. Brands need videos and photos
              from people like you.
            </p>
            <div>
              {GuidanceTips.map((tip) => (
                <div className={styles.tip} key={tip.replace(' ', '-')}>
                  <Icon size={16} name="check" />
                  {tip}
                </div>
              ))}
            </div>
          </div>
        )}
        {updatingPortfolio && (
          <div className={styles.loader}>
            <Loader />
          </div>
        )}
        {portfolio.length > 0 && !updatingPortfolio && (
          <div className={styles.portfolioContainer}>
            {portfolio
              .filter(
                ({ type }) =>
                  tab === PORTFOLIO_TABS.ALL ||
                  (tab === PORTFOLIO_TABS.PHOTOS && type === 'image') ||
                  (tab === PORTFOLIO_TABS.VIDEOS && type === 'video')
              )
              .map((media) => (
                <PortfolioItem
                  key={media.url}
                  media={media}
                  profileType={profileType}
                  onPreview={() => previewItem(media)}
                  className={styles.portfolioItem}
                  onEdit={() => onEdit(media)}
                  onRemove={() => onRemove(media)}
                />
              ))}
          </div>
        )}
        {portfolio.length === 0 && !updatingPortfolio && (
          <div className={styles.emptyPortfolio}>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={`empty-portfolio-${index}`} />
            ))}
          </div>
        )}
      </div>
      {typeof previewMedia !== 'undefined' && (
        <PreviewModal
          creatorId={creatorId}
          profileType={profileType}
          creatorName={creatorName}
          profilePicture={profilePicture}
          content={previewMedia}
          open={typeof previewMedia !== 'undefined'}
          onClose={handleClose}
          onEdit={() => onEdit(previewMedia)}
          onDelete={() => onRemove(previewMedia)}
        />
      )}
    </>
  );
};

export default Portfolio;
